import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPersistedstate from 'pinia-plugin-persistedstate'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primevue/themes'
import Lara from '@primevue/themes/lara'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'
import bg from './i18n/locales/bg.json'
import cs from './i18n/locales/cs.json'
import da from './i18n/locales/da.json'
import de from './i18n/locales/de.json'
import el from './i18n/locales/el.json'
import en from './i18n/locales/en.json'
import es from './i18n/locales/es.json'
import et from './i18n/locales/et.json'
import fi from './i18n/locales/fi.json'
import fr from './i18n/locales/fr.json'
import ga from './i18n/locales/ga.json'
import hr from './i18n/locales/hr.json'
import hu from './i18n/locales/hu.json'
import it from './i18n/locales/it.json'
import lt from './i18n/locales/lt.json'
import lv from './i18n/locales/lv.json'
import mt from './i18n/locales/mt.json'
import nl from './i18n/locales/nl.json'
import pl from './i18n/locales/pl.json'
import pt from './i18n/locales/pt.json'
import ro from './i18n/locales/ro.json'
import sk from './i18n/locales/sk.json'
import sl from './i18n/locales/sl.json'
import sv from './i18n/locales/sv.json'

import './style.css'
import 'primeicons/primeicons.css'
import 'maplibre-gl/dist/maplibre-gl.css'

// ── i18n ──────────────────────────────────────────────────────────────────────
const i18n = createI18n({
  legacy: false,
  locale: 'it',
  fallbackLocale: 'it',
  messages: {
    bg, cs, da, de, el, en, es, et, fi, fr,
    ga, hr, hu, it, lt, lv, mt, nl, pl, pt,
    ro, sk, sl, sv,
  },
})

// ── Pinia ─────────────────────────────────────────────────────────────────────
const pinia = createPinia()
pinia.use(piniaPersistedstate)

// ── App ───────────────────────────────────────────────────────────────────────
const app = createApp(App)

app.use(pinia)
app.use(router)
app.use(i18n)
const IndigoLara = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
})

app.use(PrimeVue, {
  theme: {
    preset: IndigoLara,
    options: {
      darkModeSelector: '.dark',
    },
  },
})
app.use(ToastService)
app.use(ConfirmationService)
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 60_000,          // 1 min — don't refetch if data is fresh
        refetchOnWindowFocus: false, // don't hammer API on tab focus
        retry: 1,
      },
    },
  },
})
app.directive('tooltip', Tooltip)

// ── Sentry ─────────────────────────────────────────────────────────────────────
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    sendDefaultPii: false,
  })
}

// ── Mount ─────────────────────────────────────────────────────────────────────
// Set locale from stored preference (localStorage wins, then user profile)
const authStore = useAuthStore()
const storedLocale = localStorage.getItem('ui_locale')
if (storedLocale) {
  i18n.global.locale.value = storedLocale as
    'bg' | 'cs' | 'da' | 'de' | 'el' | 'en' | 'es' | 'et' | 'fi' | 'fr' |
    'ga' | 'hr' | 'hu' | 'it' | 'lt' | 'lv' | 'mt' | 'nl' | 'pl' | 'pt' |
    'ro' | 'sk' | 'sl' | 'sv'
} else if (authStore.user?.preferred_language) {
  i18n.global.locale.value = authStore.user.preferred_language as
    'bg' | 'cs' | 'da' | 'de' | 'el' | 'en' | 'es' | 'et' | 'fi' | 'fr' |
    'ga' | 'hr' | 'hu' | 'it' | 'lt' | 'lv' | 'mt' | 'nl' | 'pl' | 'pt' |
    'ro' | 'sk' | 'sl' | 'sv'
}

app.mount('#app')
